import React, { useState, useEffect } from "react";
import { FormControl, TextField, Select, MenuItem, InputLabel } from "@mui/material";
import { toast } from "react-toastify";
import ReportTable from "./ReportTable";
import { axios_tenant } from "../../../../axios";
import { downloadReportXlsx, isReportExportError } from "./reportExportApi";
import { ENTITY_STATUS_OPTIONS, formatEntityStatus, getEntityStatusValue, normalizeEntityStatus } from "../utils/entityStatus";

const REPORT_ENDPOINT = "/kot/reports/entity-status";

const EntityStatusReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [clientFilter, setClientFilter] = useState("");
  const [assignedPpsFilter, setAssignedPpsFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");

  // Shared by the list fetch and the export so the two can never drift apart - the download
  // has to reflect exactly the filters that produced what is on screen.
  const filters = {
    clientName: clientFilter,
    assignedPps: assignedPpsFilter,
    entityType: entityTypeFilter,
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios_tenant.get(REPORT_ENDPOINT, { params: filters });
      setData(
        (response?.data || []).map((row, index) => ({
          id: index,
          ...row,
          entityStatusDisplay: formatEntityStatus(getEntityStatusValue(row)),
        })),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientFilter, assignedPpsFilter, entityTypeFilter]);

  const handleDownload = async (exportFields) => {
    try {
      setLoading(true);
      await downloadReportXlsx(REPORT_ENDPOINT, filters, exportFields, "entity_status_report.xlsx");
    } catch (error) {
      if (isReportExportError(error)) toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "clientName", headerName: "Client Name", width: 180 },
    { field: "clientNumber", headerName: "Client Number", width: 150 },
    { field: "usptoCustomerNumber", headerName: "USPTO Customer Number", width: 220 },
    { field: "assignedPps", headerName: "Assigned PPS", width: 180 },
    {
      // Derived for display only; the API knows this field as `entityStatus`.
      field: "entityStatusDisplay",
      exportField: "entityStatus",
      headerName: "Entity Type",
      width: 150,
      sortComparator: (v1, v2) => String(v1 || "").localeCompare(String(v2 || "")),
    },
    { field: "country", headerName: "Country", width: 140 },
    { field: "associateName", headerName: "Associate Name", width: 180 },
    { field: "emailId", headerName: "Email Address", width: 220 },
  ];
  
  const filtersNode = (
    <>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <TextField
          size="small"
          label="Client Name"
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
        />
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <TextField
          size="small"
          label="Assigned PPS"
          value={assignedPpsFilter}
          onChange={(e) => setAssignedPpsFilter(e.target.value)}
        />
      </FormControl>
      <FormControl size="small" >
        <InputLabel id="entity-type-label">Entity Type</InputLabel>
        <Select
          labelId="entity-type-label"
          id="entity-type-select"
          value={entityTypeFilter}
          label="Entity Type"
          onChange={(e) => setEntityTypeFilter(normalizeEntityStatus(e.target.value))}
        >
          <MenuItem value=""><em>None</em></MenuItem>
          {ENTITY_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );

  return (
    <ReportTable
      title="Reports"
      columns={columns}
      data={data}
      loading={loading}
      filtersNode={filtersNode}
      onDownload={handleDownload}
    />
  );
};

export default EntityStatusReport;
